import {
  createColumnHelper,
  type Row,
  type CellContext
} from "@tanstack/react-table";
import moment from "moment";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEnvelope, faPrint } from "@fortawesome/pro-solid-svg-icons";
import { useCallback, useEffect } from "react";

import { t, T } from "@repo/transifex";
import { type OrderPersonal } from "@repo/types";
import { isApiError } from "@repo/system";
import { priceFormatter } from "~/utils/price-formatter";
import { Button } from "~/components/Button";
import { useToast } from "~/components/hooks/use-toast";
import { usePrintOrderReceipt } from "~/hooks/queries/orders";
import {
  useAppDispatch,
  useAppContext,
  usePrintingError,
  usePrinterStatus
} from "~/providers/app";
import { useModal } from "~/components/hooks/use-modal";
import { useCurrentSession } from "~/api/sessions/hooks/use-session";

import { ToastAction } from "../../ui/toast";
import { EmailDialog } from "../email-receipt-modal";
import { RefundModal } from "../refund-modal";

export type Transactions = Pick<
  OrderPersonal,
  | "id"
  | "createdAtSeconds"
  | "profileName"
  | "paymentProvider"
  | "currency"
  | "orderLines"
  | "paidWithSaldoUnit"
  | "payments"
  | "payouts"
  | "isRefunded"
  | "isRefund"
  | "fullName"
> & { totalPriceUnit: number };

const columnHelper = createColumnHelper<Transactions>();

export const getTransactionColumns = ({
  locale,
  timezone
}: {
  locale: string;
  timezone?: string;
}) => {
  return [
    columnHelper.accessor("id", {
      id: "order_id",
      cell: (info) => {
        const value = info.getValue();
        if (!value) return null;

        return (
          <div className="whitespace-nowrap text-right font-bold uppercase">{`...${value.slice(
            -6
          )}`}</div>
        );
      },
      header: () => <T _str="ORDERID" />
    }),
    columnHelper.accessor("createdAtSeconds", {
      id: "date",
      cell: (info) => {
        const value = info.getValue();
        if (!value) return null;

        const baseMoment = moment.unix(value);

        const formattedDate = timezone
          ? baseMoment.tz(timezone).locale(locale).format("DD-MM-YYYY")
          : baseMoment.locale(locale).format("DD-MM-YYYY");

        return <div className="whitespace-nowrap">{formattedDate}</div>;
      },
      header: () => <T _str="Date" />
    }),
    columnHelper.accessor("createdAtSeconds", {
      id: "time",
      cell: (info) => {
        const value = info.getValue();
        if (!value) return null;

        const baseMoment = moment.unix(value);

        const formattedTime = timezone
          ? baseMoment.tz(timezone).format("HH:mm")
          : baseMoment.format("HH:mm");

        return <div className="whitespace-nowrap">{formattedTime}</div>;
      },
      header: () => <T _str="Time" />
    }),
    columnHelper.accessor("orderLines", {
      id: "products",
      cell: (info) => {
        const value = info.getValue();
        if (!value) return null;

        const lines = value.map((line) =>
          line.amount > 1 ? `${line.amount} x ${line.name}` : line.name
        );

        return <p>{lines.join(" / ")}</p>;
      },
      header: () => <T _str="Products" />
    }),
    columnHelper.accessor("fullName", {
      id: "user",
      cell: (info) => {
        const value = info.getValue();
        if (!value) return null;

        return <div className="whitespace-nowrap">{value}</div>;
      },
      header: () => <T _str="User" />
    }),
    columnHelper.accessor("profileName", {
      id: "admin",
      cell: (info) => {
        const value = info.getValue();
        if (!value) return null;

        return <div className="whitespace-nowrap">{value}</div>;
      },
      header: () => <T _str="Admin" />
    }),
    {
      id: "type",
      header: () => <T _str="Type" />,
      cell: ({ row }: CellContext<Transactions, number>) => {
        const payments = row.original.payments ?? [];
        const payouts = row.original.payouts ?? [];
        const hasCreditPayment = payments.some(
          (payment) => payment.paymentProvider === "credit"
        );

        const isHybridPayment =
          typeof row.original.paidWithSaldoUnit === "number" &&
          row.original.paidWithSaldoUnit > 0 &&
          !hasCreditPayment;

        // billing order refunds don't have payouts
        if (row.original.isRefund && payouts.length === 0) {
          return <T _str="Billing" />;
        }

        const paymentProvider = (payments.length > 0 ? payments : payouts).map(
          (payment) => {
            switch (payment.paymentProvider) {
              case "mobilepay_own":
                return t("MobilePay");
              case "card":
                return isHybridPayment
                  ? t("Card {credit}", { credit: t("Credit") })
                  : t("Card");
              case "billing":
                return t("Billing");
              case "cash":
                return t("Cash");
              case "credit":
                return t("Credit");
              case "swish":
                return "Swish";
              default:
                return "";
            }
          }
        );
        return (
          <div className="whitespace-nowrap">{paymentProvider.join(" / ")}</div>
        );
      }
    },
    columnHelper.accessor("totalPriceUnit", {
      id: "price",
      cell: (info) => {
        const value = info.getValue();
        if (typeof value !== "number") return null;

        return (
          <div className="text-right">
            {priceFormatter({
              value,
              currency: info.row.original.currency,
              locale
            })}
          </div>
        );
      },
      header: () => <T _str="Price" />
    }),
    {
      id: "actions",
      header: () => <T _str="Actions" />,
      cell: ({ row }: CellContext<Transactions, number>) => {
        return <ActionButtons row={row} />;
      }
    }
  ];
};

const ActionButtons = ({
  row: {
    original: { id: orderId, isRefund, isRefunded }
  }
}: {
  row: Row<Transactions>;
}) => {
  return (
    <div className="flex gap-2">
      <PrintButton orderId={orderId} />
      <EmailButton orderId={orderId} />
      <RefundButton
        orderId={orderId}
        isRefund={isRefund}
        isRefunded={isRefunded}
      />
    </div>
  );
};

const PrintButton = ({ orderId }: { orderId: string }) => {
  const {
    hardware: { printer }
  } = useAppContext();

  const { toast } = useToast();
  const dispatch = useAppDispatch();
  const {
    mutate: createReceipt,
    error,
    status,
    reset
  } = usePrintOrderReceipt();

  const printerStatus = usePrinterStatus();
  const printingError = usePrintingError();

  const handlePrintReceipt = useCallback(() => {
    if (status === "pending" || printerStatus === "loading") return;

    toast({
      title: t("Printing receipt"),
      description: t("Please wait"),
      duration: Infinity
    });

    createReceipt({ orderId, userId: null });
  }, [createReceipt, orderId, printerStatus, status, toast]);

  useEffect(() => {
    if (printerStatus === "error" || status === "error") {
      const errorMessage = isApiError(error)
        ? error.body.message
        : printingError;

      toast({
        title: t("Failed to print"),
        description: errorMessage,
        variant: "destructive",
        action: (
          <ToastAction
            altText={t("Try again")}
            onClick={() => {
              reset();
              dispatch({ type: "PRINTER_PRINT_RECEIPT_RESET" });
              handlePrintReceipt();
            }}
          >
            {t("Try again")}
          </ToastAction>
        )
      });
    }
  }, [
    printerStatus,
    status,
    printingError,
    toast,
    reset,
    dispatch,
    error,
    handlePrintReceipt
  ]);

  useEffect(() => {
    if (printerStatus === "success") {
      toast({
        title: t("Receipt printed"),
        variant: "success"
      });
      dispatch({ type: "PRINTER_PRINT_RECEIPT_RESET" });
    }
  }, [printerStatus, toast, dispatch]);

  return printer ? (
    <Button
      variant="secondary"
      onClick={handlePrintReceipt}
      disabled={status === "pending" || printerStatus === "loading"}
    >
      <FontAwesomeIcon icon={faPrint} />
    </Button>
  ) : null;
};

const EmailButton = ({ orderId }: { orderId: string }) => {
  const { showModal, hideModal } = useModal();

  return (
    <Button
      variant="secondary"
      onClick={() => {
        showModal({
          component: <EmailDialog orderId={orderId} onClose={hideModal} />
        });
      }}
    >
      <FontAwesomeIcon icon={faEnvelope} />
    </Button>
  );
};

const RefundButton = ({
  orderId,
  isRefund,
  isRefunded
}: {
  orderId: string;
  isRefund?: boolean;
  isRefunded?: boolean;
}) => {
  const { showModal, hideModal } = useModal();

  const { data: session } = useCurrentSession();

  const isRefundDisabled = !session?.isValid || isRefunded;

  const handleRefund = () => {
    showModal({
      component: <RefundModal onClose={hideModal} orderId={orderId} />
    });
  };

  return (
    <Button
      variant="danger-light"
      onClick={handleRefund}
      disabled={isRefundDisabled}
      hidden={isRefund}
    >
      <T _str="Refund" />
    </Button>
  );
};
